import * as React from 'react'
import { MagnifyingGlass, X } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { type FieldStatus } from './field'
import { InputGroup, InputGroupInput, InputGroupAddon } from './input-group'

/**
 * SearchBar — an inline search field built on the `InputGroup` shell: a leading
 * magnifier, a borderless input, an optional clear (×) button, and two slots
 * for a leading selector (`leftSlot`) or a trailing button (`rightSlot`). Those
 * slots accept any node today; the dedicated `Select` control (Phase 3) drops
 * straight into `leftSlot` with no structural change.
 *
 *   <SearchBar placeholder="Search" clearable />
 *   <SearchBar rightSlot={<Button size="sm">Search</Button>} />
 *   <SearchBar leftSlot={<CategorySelect />} />
 *
 * Controlled (`value` + `onChange`/`onValueChange`) or uncontrolled
 * (`defaultValue`). `onSearch` fires on Enter. Status + size + disabled mirror
 * `Input`, and the shell shares its border + focus-ring tokens.
 */
export interface SearchBarProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size'
> {
  size?: 'sm' | 'md' | 'lg'
  status?: FieldStatus
  /** Segmented slot at the far left (e.g. a category selector). */
  leftSlot?: React.ReactNode
  /** Segmented slot at the far right (e.g. a "Search" button). */
  rightSlot?: React.ReactNode
  /** Show a trailing × to clear the field once it has text. */
  clearable?: boolean
  /** Called after the field is cleared via the × button. */
  onClear?: () => void
  /** Convenience change handler with the raw string value. */
  onValueChange?: (value: string) => void
  /** Fires with the current value when Enter is pressed. */
  onSearch?: (value: string) => void
  /** Classes on the `InputGroup` shell (e.g. a fixed width). */
  containerClassName?: string
}

export const SearchBar = React.forwardRef<HTMLInputElement, SearchBarProps>(
  (
    {
      size = 'md',
      status,
      leftSlot,
      rightSlot,
      clearable = false,
      onClear,
      onValueChange,
      onSearch,
      containerClassName,
      className,
      placeholder = 'Search',
      disabled = false,
      value,
      defaultValue,
      onChange,
      onKeyDown,
      ...rest
    },
    forwardedRef
  ) => {
    const innerRef = React.useRef<HTMLInputElement>(null)
    React.useImperativeHandle(forwardedRef, () => innerRef.current!, [])

    const isControlled = value !== undefined
    const [internalValue, setInternalValue] = React.useState(
      defaultValue?.toString() ?? ''
    )
    const currentValue = isControlled
      ? (value?.toString() ?? '')
      : internalValue

    const iconSize = size === 'lg' ? 'size-5' : 'size-4'
    const showClear = clearable && !disabled && currentValue.length > 0

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) setInternalValue(e.target.value)
      onChange?.(e)
      onValueChange?.(e.target.value)
    }

    const handleClear = () => {
      const input = innerRef.current
      // Drive the clear through a native input event so a controlled consumer's
      // `onChange` fires too (not just `onValueChange`) — then handleChange runs
      // the normal path (onChange + onValueChange + uncontrolled state sync).
      if (input) {
        const setValue = Object.getOwnPropertyDescriptor(
          HTMLInputElement.prototype,
          'value'
        )?.set
        setValue?.call(input, '')
        input.dispatchEvent(new Event('input', { bubbles: true }))
        input.focus()
      } else {
        if (!isControlled) setInternalValue('')
        onValueChange?.('')
      }
      onClear?.()
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') onSearch?.(currentValue)
      onKeyDown?.(e)
    }

    return (
      <InputGroup
        size={size}
        status={status}
        disabled={disabled}
        className={containerClassName}
      >
        {leftSlot && (
          <InputGroupAddon align="leading" divided>
            {leftSlot}
          </InputGroupAddon>
        )}
        <span
          aria-hidden="true"
          className={cn(
            'flex shrink-0 items-center pl-3',
            disabled ? 'text-fbui-gray-300' : 'text-fbui-gray-400'
          )}
        >
          <MagnifyingGlass className={iconSize} />
        </span>
        <InputGroupInput
          ref={innerRef}
          type="search"
          placeholder={placeholder}
          disabled={disabled}
          value={isControlled ? value : undefined}
          defaultValue={isControlled ? undefined : defaultValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          className={cn('pl-2', className)}
          {...rest}
        />
        {showClear && (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear search"
            className="mr-1 inline-flex size-6 shrink-0 items-center justify-center rounded-md text-fbui-gray-400 outline-none transition-colors hover:bg-fbui-gray-100 hover:text-fbui-gray-600 focus-visible:ring-2 focus-visible:ring-fbui-ring-default"
          >
            <X weight="bold" className="size-3.5" aria-hidden="true" />
          </button>
        )}
        {rightSlot && (
          <InputGroupAddon align="trailing" divided>
            {rightSlot}
          </InputGroupAddon>
        )}
      </InputGroup>
    )
  }
)
SearchBar.displayName = 'SearchBar'
